import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeader, getUser } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Eye, Upload, Download, Edit, MessageSquare, CheckCircle } from 'lucide-react';
import { downloadFile } from '../utils/fileUtils';

const PurchaseRequest = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [prs, setPrs] = useState([]);
  const [activeTab, setActiveTab] = useState('myRequests');
  const [vendors, setVendors] = useState([]);
  const [entities, setEntities] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedPr, setSelectedPr] = useState(null);
  const [uploadInvoiceDialogOpen, setUploadInvoiceDialogOpen] = useState(false);
  const [uploadInvoiceData, setUploadInvoiceData] = useState({
    entity_id: '',
    vendor_id: '',
    reason: '',
    base_amount: '',
    inclusive_tax: 'No',
    invoice_date: '',
    tax_amount: '',
    total_amount: ''
  });
  const [uploadInvoiceFile, setUploadInvoiceFile] = useState(null);
  const [editUploadInvoiceMode, setEditUploadInvoiceMode] = useState(false);
  const [editUploadInvoiceId, setEditUploadInvoiceId] = useState(null);
  const [confirmDeliveryDialogOpen, setConfirmDeliveryDialogOpen] = useState(false);
  const [selectedPrForDelivery, setSelectedPrForDelivery] = useState(null);
  const [formData, setFormData] = useState({
    entity_id: '',
    vendor_id: '',
    reason: '',
    base_amount: '',
    inclusive_tax: 'No',
    tax_amount: ''
  });
  const [proformaFile, setProformaFile] = useState(null);
  const [taxInvoiceFile, setTaxInvoiceFile] = useState(null);
  const [taxInvoiceData, setTaxInvoiceData] = useState({
    invoice_date: '',
    base_amount: '',
    tax_amount: '',
    total_amount: ''
  });
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyDocs, setReplyDocs] = useState([]);
  const [uploadedReplyDocUrls, setUploadedReplyDocUrls] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    entity_id: '',
    vendor_id: '',
    cost_centre: '',
    reason: '',
    base_amount: '',
    inclusive_tax: 'No'
  });
  const [editProformaFile, setEditProformaFile] = useState(null);
  
  // Advance Request states
  const [advanceRequestDialogOpen, setAdvanceRequestDialogOpen] = useState(false);
  const [advanceRequestData, setAdvanceRequestData] = useState({
    entity_id: '',
    vendor_id: '',
    reason: '',
    base_amount: '',
    inclusive_tax: 'No',
    advance_amount: '',
    tax_amount: '',
    total_amount: ''
  });
  const [advanceProformaFile, setAdvanceProformaFile] = useState(null);
  
  // Pagination & Filter states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [costCenterFilter, setCostCenterFilter] = useState('all');

  useEffect(() => {
    fetchPRs();
    fetchVendors();
    fetchEntities();
    fetchCostCentres();
  }, []);

  const fetchPRs = async () => {
    try {
      const response = await axios.get(`${API}/purchase-requests`, {
        headers: getAuthHeader()
      });
      console.log('PRs fetched:', response.data);
      // API returns array directly, not wrapped in an object
      const prData = Array.isArray(response.data) ? response.data : (response.data.purchase_requests || []);
      console.log('PR array:', prData);
      setPrs(prData);
    } catch (error) {
      console.error('Error fetching PRs:', error);
      toast.error('Failed to fetch purchase requests');
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`, {
        headers: getAuthHeader()
      });
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchEntities = async () => {
    try {
      const response = await axios.get(`${API}/entities/options`, {
        headers: getAuthHeader()
      });
      setEntities(response.data || []);
    } catch (error) {
      console.error('Error fetching entities:', error);
    }
  };

  const fetchCostCentres = async () => {
    try {
      const response = await axios.get(`${API}/cost-centres`, {
        headers: getAuthHeader()
      });
      setCostCentres(response.data.cost_centres || []);
    } catch (error) {
      console.error('Error fetching cost centres:', error);
    }
  };

  const handleCreatePR = async (e) => {
    e.preventDefault();
    if (!formData.entity_id) {
      toast.error('Please select an entity');
      return;
    }
    if (!proformaFile) {
      toast.error('Please upload proforma invoice');
      return;
    }

    // Validate tax amount if tax inclusive is Yes
    if (formData.inclusive_tax === 'Yes') {
      if (formData.tax_amount === '' || parseFloat(formData.tax_amount) < 0) {
        toast.error('Please enter a valid tax amount');
        return;
      }
    }

    setLoading(true);
    try {
      // Prepare payload - only include fields that are needed
      const payload = {
        entity_id: formData.entity_id,
        vendor_id: formData.vendor_id,
        reason: formData.reason,
        base_amount: parseFloat(formData.base_amount) || 0,
        inclusive_tax: formData.inclusive_tax
      };
      
      if (formData.inclusive_tax === 'Yes') {
        payload.tax_amount = parseFloat(formData.tax_amount) || 0;
        payload.total_amount = (parseFloat(formData.base_amount) || 0) + (parseFloat(formData.tax_amount) || 0);
      }

      const prResponse = await axios.post(`${API}/purchase-requests`, payload, {
        headers: getAuthHeader()
      });

      const prId = prResponse.data.id;

      const formDataFile = new FormData();
      formDataFile.append('file', proformaFile);

      await axios.post(`${API}/purchase-requests/${prId}/upload-proforma`, formDataFile, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Purchase request created successfully');
      setDialogOpen(false);
      setFormData({ entity_id: '', vendor_id: '', reason: '', base_amount: '', inclusive_tax: 'No', tax_amount: '' });
      setProformaFile(null);
      fetchPRs();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      // Handle validation errors that come as arrays
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg || e).join(', '));
      } else {
        toast.error(errorMsg || 'Failed to create purchase request');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUploadTaxInvoice = async (pr) => {
    if (!taxInvoiceFile || !taxInvoiceData.invoice_date) {
      toast.error('Please fill all tax invoice details');
      return;
    }

    setLoading(true);
    try {
      // Upload file FIRST to save URL to database
      const formDataFile = new FormData();
      formDataFile.append('file', taxInvoiceFile);

      await axios.post(`${API}/purchase-requests/${pr.id}/upload-tax-invoice-file`, formDataFile, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      // Then upload metadata (triggers email after URL is saved)
      await axios.post(
        `${API}/purchase-requests/${pr.id}/upload-tax-invoice`,
        taxInvoiceData,
        { headers: getAuthHeader() }
      );

      toast.success('Tax invoice uploaded successfully');
      setDetailsDialogOpen(false);
      setTaxInvoiceFile(null);
      setTaxInvoiceData({ invoice_date: '' });
      fetchPRs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload tax invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadReplyDoc = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `${API}/purchase-requests/${selectedPr.id}/upload-reply-docs`,
        formData,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      return response.data.url;
    } catch (error) {
      toast.error('Failed to upload document');
      return null;
    }
  };

  const handleReplyToInfoRequest = async () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a response');
      return;
    }

    setLoading(true);
    try {
      // Upload all reply documents first
      const docUrls = [];
      for (const file of replyDocs) {
        const url = await handleUploadReplyDoc(file);
        if (url) docUrls.push(url);
      }

      await axios.post(
        `${API}/purchase-requests/${selectedPr.id}/submit-additional-info`,
        { 
          additional_info: replyMessage,
          docs: docUrls
        },
        { headers: getAuthHeader() }
      );

      toast.success('Response submitted successfully');
      setReplyDialogOpen(false);
      setReplyMessage('');
      setReplyDocs([]);
      setUploadedReplyDocUrls([]);
      setSelectedPr(null);
      fetchPRs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit response');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPR = async (e) => {
    e.preventDefault();
    if (!editFormData.entity_id) {
      toast.error('Please select an entity');
      return;
    }
    setLoading(true);
    try {
      // Update PR details
      await axios.put(
        `${API}/purchase-requests/${selectedPr.id}`,
        editFormData,
        { headers: getAuthHeader() }
      );

      // If new proforma file is selected, upload it
      if (editProformaFile) {
        const formData = new FormData();
        formData.append('file', editProformaFile);
        
        await axios.post(
          `${API}/purchase-requests/${selectedPr.id}/upload-proforma`,
          formData,
          { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
        );
      }

      toast.success('Purchase request updated successfully');
      setEditMode(false);
      setEditProformaFile(null);
      setDetailsDialogOpen(false);
      fetchPRs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update purchase request');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get filename with correct extension from S3 key
  const getFilenameWithExtension = (s3Key, defaultName) => {
    if (!s3Key) return defaultName;
    
    // Extract original filename from S3 key (e.g., "proforma/uuid_invoice.png" -> "invoice.png")
    const parts = s3Key.split('/');
    const filename = parts[parts.length - 1];
    
    // Extract extension from original filename
    const extensionMatch = filename.match(/\.[^.]+$/);
    const extension = extensionMatch ? extensionMatch[0] : '.pdf';
    
    // Return default name with correct extension
    return defaultName.replace(/\.[^.]*$/, '') + extension;
  };

  // Helper function to get advance payment status badge
  const getAdvanceStatusBadge = (pr) => {
    if (!pr.is_advance_payment) return null;
    
    // Don't show badge if status is none or undefined
    if (!pr.advance_payment_status || pr.advance_payment_status === 'none') return null;
    
    const statusConfig = {
      'pending_ppa': { label: 'Pending PPA', color: 'bg-yellow-100 text-yellow-800' },
      'approved_pp': { label: 'Approved PP', color: 'bg-blue-100 text-blue-800' },
      'ppa_approved': { label: 'PP Approved', color: 'bg-green-100 text-green-800' },
      'ppa_rejected': { label: 'PP Rejected', color: 'bg-red-100 text-red-800' },
      'advance_paid': { label: 'Advance Paid', color: 'bg-emerald-100 text-emerald-800' },
      'paid': { label: 'Advance Paid', color: 'bg-emerald-100 text-emerald-800' }
    };
    
    const config = statusConfig[pr.advance_payment_status];
    if (!config) return null;
    
    return (
      <Badge className={`${config.color} text-xs w-auto inline-flex`}>
        {config.label}
      </Badge>
    );
  };

  const handleUploadInvoiceSubmit = async () => {
    // Validation
    if (!uploadInvoiceData.entity_id || !uploadInvoiceData.vendor_id || !uploadInvoiceData.reason || 
        !uploadInvoiceData.base_amount || !uploadInvoiceData.invoice_date) {
      toast.error('Please fill all required fields');
      return;
    }

    // Validate tax fields if tax inclusive is Yes
    if (uploadInvoiceData.inclusive_tax === 'Yes') {
      if (uploadInvoiceData.tax_amount === '' || parseFloat(uploadInvoiceData.tax_amount) < 0) {
        toast.error('Please enter a valid tax amount (0 or greater)');
        return;
      }
    }
    
    if (!editUploadInvoiceMode && !uploadInvoiceFile) {
      toast.error('Please upload invoice file');
      return;
    }
    
    setLoading(true);
    try {
      // Prepare payload with tax fields if tax inclusive is Yes
      const payload = {
        entity_id: uploadInvoiceData.entity_id,
        vendor_id: uploadInvoiceData.vendor_id,
        reason: uploadInvoiceData.reason,
        base_amount: parseFloat(uploadInvoiceData.base_amount),
        inclusive_tax: uploadInvoiceData.inclusive_tax,
        invoice_date: uploadInvoiceData.invoice_date
      };

      if (uploadInvoiceData.inclusive_tax === 'Yes') {
        payload.tax_amount = parseFloat(uploadInvoiceData.tax_amount) || 0;
        payload.total_amount = parseFloat(uploadInvoiceData.total_amount);
      }

      if (editUploadInvoiceMode) {
        // Edit mode: Update existing upload invoice
        await axios.put(
          `${API}/upload-invoice-requests/${editUploadInvoiceId}`,
          payload,
          { headers: getAuthHeader() }
        );
        
        // Upload new file if selected
        if (uploadInvoiceFile) {
          const formData = new FormData();
          formData.append('file', uploadInvoiceFile);
          
          await axios.post(
            `${API}/upload-invoice-requests/${editUploadInvoiceId}/upload-invoice-file`,
            formData,
            { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
          );
        }
        
        toast.success('Upload invoice request updated successfully');
      } else {
        // Create mode
        const response = await axios.post(
          `${API}/upload-invoice-requests`,
          payload,
          { headers: getAuthHeader() }
        );
        
        const prId = response.data.id;
        
        // Step 2: Upload invoice file
        const formData = new FormData();
        formData.append('file', uploadInvoiceFile);
        
        await axios.post(
          `${API}/upload-invoice-requests/${prId}/upload-invoice-file`,
          formData,
          { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
        );
        
        toast.success('Upload invoice request submitted successfully');
      }
      
      setUploadInvoiceDialogOpen(false);
      
      // Reset form
      setUploadInvoiceData({
        entity_id: '',
        vendor_id: '',
        reason: '',
        base_amount: '',
        inclusive_tax: 'No',
        invoice_date: '',
        tax_amount: '',
        total_amount: ''
      });
      setUploadInvoiceFile(null);
      setEditUploadInvoiceMode(false);
      setEditUploadInvoiceId(null);
      
      fetchPRs(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleAdvanceRequestSubmit = async () => {
    // Check if we're editing an existing request
    const isEditing = selectedPr && selectedPr.is_advance_payment && selectedPr.status === 'rejected';
    
    // Validation
    if (!advanceRequestData.entity_id || !advanceRequestData.vendor_id || !advanceRequestData.reason || 
        !advanceRequestData.base_amount || !advanceRequestData.advance_amount) {
      toast.error('Please fill all required fields');
      return;
    }

    // Validate tax fields if tax inclusive is Yes
    if (advanceRequestData.inclusive_tax === 'Yes') {
      if (advanceRequestData.tax_amount === '' || parseFloat(advanceRequestData.tax_amount) < 0) {
        toast.error('Please enter a valid tax amount (0 or greater)');
        return;
      }
    }
    
    // Only require file upload for new requests
    if (!isEditing && !advanceProformaFile) {
      toast.error('Please upload proforma invoice');
      return;
    }
    
    // Validate advance amount
    const baseAmount = parseFloat(advanceRequestData.base_amount);
    const advanceAmount = parseFloat(advanceRequestData.advance_amount);
    const totalAmount = parseFloat(advanceRequestData.total_amount);
    
    if (advanceAmount <= 0) {
      toast.error('Advance amount must be greater than 0');
      return;
    }
    
    if (advanceAmount > totalAmount) {
      toast.error('Advance amount cannot be greater than total amount');
      return;
    }
    
    setLoading(true);
    try {
      const requestData = {
        entity_id: advanceRequestData.entity_id,
        vendor_id: advanceRequestData.vendor_id,
        reason: advanceRequestData.reason,
        base_amount: baseAmount,
        inclusive_tax: advanceRequestData.inclusive_tax,
        tax_amount: parseFloat(advanceRequestData.tax_amount) || 0,
        total_amount: totalAmount,
        advance_amount: advanceAmount
      };
      
      let prId;
      
      if (isEditing) {
        // Update existing request
        await axios.put(
          `${API}/advance-request/${selectedPr.id}`,
          requestData,
          { headers: getAuthHeader() }
        );
        prId = selectedPr.id;
        toast.success('Advance payment request updated successfully');
      } else {
        // Create new request
        const response = await axios.post(
          `${API}/advance-request`,
          requestData,
          { headers: getAuthHeader() }
        );
        prId = response.data.id;
        toast.success('Advance payment request submitted successfully');
      }
      
      // Upload/update proforma invoice if a new file is provided
      if (advanceProformaFile) {
        const formData = new FormData();
        formData.append('file', advanceProformaFile);
        
        await axios.post(
          `${API}/purchase-requests/${prId}/upload-proforma`,
          formData,
          { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
        );
      }
      
      setAdvanceRequestDialogOpen(false);
      
      // Reset form
      setAdvanceRequestData({
        entity_id: '',
        vendor_id: '',
        reason: '',
        base_amount: '',
        inclusive_tax: 'No',
        advance_amount: '',
        tax_amount: '',
        total_amount: ''
      });
      setAdvanceProformaFile(null);
      setSelectedPr(null);
      
      fetchPRs(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit advance request');
    } finally {
      setLoading(false);
    }
  };

  const openEditUploadInvoice = (pr) => {
    setEditUploadInvoiceMode(true);
    setEditUploadInvoiceId(pr.id);
    setUploadInvoiceData({
      entity_id: pr.entity_id || '',
      vendor_id: pr.vendor_id,
      reason: pr.reason,
      base_amount: pr.base_amount,
      inclusive_tax: pr.inclusive_tax || 'No',
      invoice_date: pr.tax_invoice_date,
      tax_amount: pr.tax_amount || '0',
      total_amount: pr.total_amount || pr.base_amount
    });
    setUploadInvoiceFile(null);
    setUploadInvoiceDialogOpen(true);
  };

  const openDetailsDialog = (pr) => {
    setSelectedPr(pr);
    setEditMode(false); // Reset edit mode when opening a new PR
    setEditProformaFile(null); // Reset proforma file
    setEditFormData({
      entity_id: pr.entity_id || '',
      vendor_id: pr.vendor_id,
      cost_centre: pr.cost_centre,
      reason: pr.reason,
      base_amount: pr.base_amount,
      inclusive_tax: pr.inclusive_tax || 'No'
    });
    setDetailsDialogOpen(true);
  };

  const openEditDialog = (pr) => {
    setSelectedPr(pr);
    
    // Check if this is an advance payment request
    if (pr.is_advance_payment) {
      // Open advance request edit dialog
      setAdvanceRequestData({
        entity_id: pr.entity_id || '',
        vendor_id: pr.vendor_id,
        reason: pr.reason,
        base_amount: pr.base_amount,
        inclusive_tax: pr.inclusive_tax || 'No',
        advance_amount: pr.advance_amount,
        tax_amount: pr.tax_amount || '0',
        total_amount: pr.total_amount || pr.base_amount
      });
      setAdvanceRequestDialogOpen(true);
    } else {
      // Open regular PR edit dialog
      setEditMode(true);
      setEditProformaFile(null);
      setEditFormData({
        entity_id: pr.entity_id || '',
        vendor_id: pr.vendor_id,
        cost_centre: pr.cost_centre,
        reason: pr.reason,
        base_amount: pr.base_amount,
        inclusive_tax: pr.inclusive_tax || 'No'
      });
      setDetailsDialogOpen(true);
    }
  };

  const openConfirmDeliveryDialog = (pr) => {
    setSelectedPrForDelivery(pr);
    const baseAmt = pr.base_amount || 0;
    const taxAmt = pr.tax_amount || 0;
    const totalAmt = pr.total_amount || (baseAmt + taxAmt);
    setTaxInvoiceData({ 
      invoice_date: '',
      base_amount: baseAmt || '',
      tax_amount: taxAmt || '0',
      total_amount: totalAmt || ''
    });
    setTaxInvoiceFile(null);
    setConfirmDeliveryDialogOpen(true);
  };

  const handleConfirmDeliveryAndUploadInvoice = async () => {
    if (!taxInvoiceFile || !taxInvoiceData.invoice_date) {
      toast.error('Please fill all tax invoice details');
      return;
    }

    // Validate tax fields if tax inclusive is Yes
    if (selectedPrForDelivery?.inclusive_tax === 'Yes') {
      if (!taxInvoiceData.base_amount || taxInvoiceData.base_amount <= 0) {
        toast.error('Please enter a valid base amount');
        return;
      }
      if (taxInvoiceData.tax_amount === '' || parseFloat(taxInvoiceData.tax_amount) < 0) {
        toast.error('Please enter a valid tax amount (0 or greater)');
        return;
      }
    }

    setLoading(true);
    try {
      // Step 1: Confirm Delivery
      await axios.post(
        `${API}/purchase-requests/${selectedPrForDelivery.id}/confirm-delivery`,
        {},
        { headers: getAuthHeader() }
      );

      // Step 2: Upload Tax Invoice file FIRST (must save URL to DB before triggering email)
      const formData = new FormData();
      formData.append('file', taxInvoiceFile);

      await axios.post(
        `${API}/purchase-requests/${selectedPrForDelivery.id}/upload-tax-invoice-file`,
        formData,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );

      // Step 3: Upload Tax Invoice metadata (triggers email after file URL is saved)
      const invoicePayload = {
        invoice_date: taxInvoiceData.invoice_date
      };
      
      // Include tax fields if tax inclusive is Yes
      if (selectedPrForDelivery?.inclusive_tax === 'Yes') {
        invoicePayload.base_amount = parseFloat(taxInvoiceData.base_amount);
        invoicePayload.tax_amount = parseFloat(taxInvoiceData.tax_amount) || 0;
        invoicePayload.total_amount = parseFloat(taxInvoiceData.total_amount);
      }

      await axios.post(
        `${API}/purchase-requests/${selectedPrForDelivery.id}/upload-tax-invoice`,
        invoicePayload,
        { headers: getAuthHeader() }
      );

      toast.success('Delivery confirmed and tax invoice uploaded successfully');
      setConfirmDeliveryDialogOpen(false);
      setTaxInvoiceData({ 
        invoice_date: '',
        base_amount: '',
        tax_amount: '',
        total_amount: ''
      });
      setTaxInvoiceFile(null);
      fetchPRs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (pr) => {
    const status = pr.status;
    const isUploadInvoice = pr.is_upload_invoice;
    
    const statusConfig = {
      'pending_approval': { label: 'Pending Function Head Approval', color: 'bg-yellow-100 text-yellow-800' },
      'approved': { label: 'PO Issued To Vendor', color: 'bg-green-100 text-green-800' },
      'rejected': { 
        label: pr.rejected_by && pr.rejected_by.includes('finance') ? 'Rejected By Finance Head' : 'Rejected By Function Head', 
        color: 'bg-red-100 text-red-800' 
      },
      'pending_finance_approval': { label: 'Pending Finance Approval', color: 'bg-cyan-100 text-cyan-800' },
      'tax_invoice_uploaded': { label: 'Pending Finance Approval', color: 'bg-blue-100 text-blue-800' },
      'additional_info_requested': { label: 'Addl Info Requested', color: 'bg-orange-100 text-orange-800' },
      'finance_approved': { label: 'Approved For Payment', color: 'bg-purple-100 text-purple-800' },
      'paid': { label: 'Paid', color: 'bg-emerald-100 text-emerald-800' }
    };

    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    
    return (
      <div className="flex flex-col gap-1 items-start">
        {pr.is_advance_payment && (
          <Badge className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <CheckCircle className="w-3 h-3" />
            <span className="text-xs font-semibold">AR</span>
          </Badge>
        )}
        {!pr.is_advance_payment && !isUploadInvoice && (
          <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <CheckCircle className="w-3 h-3" />
            <span className="text-xs font-semibold">PR</span>
          </Badge>
        )}
        {isUploadInvoice && (
          <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <Upload className="w-3 h-3" />
            <span className="text-xs font-semibold">UI</span>
          </Badge>
        )}
        <Badge className={`${config.color} inline-flex w-auto`}>{config.label}</Badge>
      </div>
    );
  };

  const getVendorName = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor?.legal_entity_name || vendor?.trade_name || 'N/A';
  };

  const getEntityName = (entityId, entityName) => {
    if (entityName) return entityName;
    if (!entityId) return 'N/A';
    const entity = entities.find(e => e.id === entityId);
    return entity?.name || 'N/A';
  };

  // Get unique cost centers for filter dropdown
  const getUniqueCostCenters = () => {
    const costCenters = new Set();
    prs.forEach(pr => {
      if (pr.cost_centre) {
        costCenters.add(pr.cost_centre);
      }
    });
    return Array.from(costCenters).sort();
  };

  const uniqueCostCenters = getUniqueCostCenters();

  // Filter PRs based on active tab and filters
  const getFilteredPRs = () => {
    let filtered = [];
    
    if (activeTab === 'myRequests') {
      // My Requests: Regular purchase requests (not upload invoice, not advance payment), not paid, not finance-rejected
      filtered = prs.filter(pr => 
        !pr.is_upload_invoice && 
        !pr.is_advance_payment &&
        pr.status !== 'paid' &&
        !(pr.status === 'rejected' && pr.rejected_by && pr.rejected_by.includes('finance'))
      );
    } else if (activeTab === 'uploadInvoice') {
      // Upload Invoice: Only upload invoice requests, not paid, not finance-rejected
      filtered = prs.filter(pr => 
        pr.is_upload_invoice === true && 
        pr.status !== 'paid' &&
        !(pr.status === 'rejected' && pr.rejected_by && pr.rejected_by.includes('finance'))
      );
    } else if (activeTab === 'advanceRequests') {
      // Advance Requests: Only advance payment requests, not paid, not finance-rejected
      filtered = prs.filter(pr => 
        pr.is_advance_payment === true && 
        pr.status !== 'paid' &&
        !(pr.status === 'rejected' && pr.rejected_by && pr.rejected_by.includes('finance'))
      );
    } else {
      // History: All paid requests AND finance-rejected requests (all types)
      filtered = prs.filter(pr => 
        pr.status === 'paid' ||
        (pr.status === 'rejected' && pr.rejected_by && pr.rejected_by.includes('finance'))
      );
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(pr => 
        pr.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getVendorName(pr.vendor_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getEntityName(pr.entity_id, pr.entity_name).toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.cost_centre?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(pr => pr.status === statusFilter);
    }

    // Apply cost center filter
    if (costCenterFilter !== 'all') {
      filtered = filtered.filter(pr => pr.cost_centre === costCenterFilter);
    }

    return filtered;
  };

  const filteredPRs = getFilteredPRs();
  const totalPages = Math.ceil(filteredPRs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPRs = filteredPRs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, statusFilter, costCenterFilter]);

  console.log('All PRs:', prs);
  console.log('Filtered PRs:', filteredPRs);
  console.log('Active tab:', activeTab);

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
            My Requests
          </h1>
          <p className="text-gray-600 mt-1">Create and track your requests</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Purchase Request
          </Button>
          <Button
            onClick={() => setUploadInvoiceDialogOpen(true)}
            className="bg-purple-600 hover:bg-purple-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Upload Invoice
          </Button>
          <Button
            onClick={() => setAdvanceRequestDialogOpen(true)}
            className="bg-amber-600 hover:bg-amber-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Raise Advance Request
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('myRequests')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'myRequests'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            My Purchase Requests ({prs.filter(pr => !pr.is_upload_invoice && !pr.is_advance_payment && pr.status !== 'paid' && !(pr.status === 'rejected' && pr.rejected_by && pr.rejected_by.includes('finance'))).length})
          </button>
          <button
            onClick={() => setActiveTab('uploadInvoice')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'uploadInvoice'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Upload Invoice ({prs.filter(pr => pr.is_upload_invoice === true && pr.status !== 'paid' && !(pr.status === 'rejected' && pr.rejected_by && pr.rejected_by.includes('finance'))).length})
          </button>
          <button
            onClick={() => setActiveTab('advanceRequests')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'advanceRequests'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Advance Requests ({prs.filter(pr => pr.is_advance_payment === true && pr.status !== 'paid' && !(pr.status === 'rejected' && pr.rejected_by && pr.rejected_by.includes('finance'))).length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            History ({prs.filter(pr => pr.status === 'paid' || (pr.status === 'rejected' && pr.rejected_by && pr.rejected_by.includes('finance'))).length})
          </button>
        </nav>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {activeTab === 'myRequests' ? 'My Purchase Requests' : 
             activeTab === 'uploadInvoice' ? 'Upload Invoice Requests' : 
             activeTab === 'advanceRequests' ? 'Advance Payment Requests' : 
             'Payment History'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filter and Search Controls */}
          <div className="mb-4 flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-3 items-center flex-1">
              <input
                type="text"
                placeholder="Search by reason, vendor, entity, cost center..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1 max-w-md"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Status</option>
                {activeTab === 'active' ? (
                  <>
                    <option value="pending_approval">Pending Approval</option>
                    <option value="approved">PO Issued</option>
                    <option value="rejected">Rejected</option>
                    <option value="pending_finance_approval">Finance Review</option>
                    <option value="tax_invoice_uploaded">Finance Review</option>
                    <option value="additional_info_requested">Info Requested</option>
                    <option value="finance_approved">Approved For Payment</option>
                  </>
                ) : (
                  <>
                    <option value="paid">Paid</option>
                    <option value="rejected">Rejected</option>
                  </>
                )}
              </select>
              <select
                value={costCenterFilter}
                onChange={(e) => setCostCenterFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Cost Centers</option>
                {uniqueCostCenters.map(cc => (
                  <option key={cc} value={cc}>{cc}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="text-sm text-gray-600">per page</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Reason</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Vendor</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Entity</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Cost Center</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentPRs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      No purchase requests found
                    </td>
                  </tr>
                ) : (
                  currentPRs.map((pr) => (
                    <tr key={pr.id}>
                      <td className="px-4 py-3 text-sm text-gray-900">{pr.reason}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{getVendorName(pr.vendor_id)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{getEntityName(pr.entity_id, pr.entity_name)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{pr.cost_centre}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        ₹{(pr.total_amount || pr.base_amount || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1 items-start">
                          {getStatusBadge(pr)}
                          {getAdvanceStatusBadge(pr)}
                          {pr.hold_status && (
                            <Badge className="bg-red-100 text-red-800 w-auto inline-flex">On Hold</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDetailsDialog(pr)}
                            className="flex items-center gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                          {pr.is_upload_invoice && pr.status === 'rejected' && !(pr.rejected_by && pr.rejected_by.includes('finance')) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditUploadInvoice(pr)}
                              className="flex items-center gap-2 border-purple-500 text-purple-600 hover:bg-purple-50"
                            >
                              <Edit className="w-4 h-4" />
                              Edit
                            </Button>
                          )}
                          {!pr.is_upload_invoice && pr.status === 'rejected' && !(pr.rejected_by && pr.rejected_by.includes('finance')) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(pr)}
                              className="flex items-center gap-2 border-indigo-500 text-indigo-600 hover:bg-indigo-50"
                            >
                              <Edit className="w-4 h-4" />
                              Edit
                            </Button>
                          )}
                          {!pr.is_upload_invoice && pr.status === 'approved' && !pr.delivery_confirmed && (
                            <Button
                              size="sm"
                              onClick={() => openConfirmDeliveryDialog(pr)}
                              className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                              disabled={pr.is_advance_payment && pr.advance_payment_status !== 'paid'}
                              title={pr.is_advance_payment && pr.advance_payment_status !== 'paid' ? 'Advance payment must be completed before confirming delivery' : 'Confirm that goods/services have been delivered'}
                            >
                              <CheckCircle className="w-4 h-4" />
                              Confirm Delivery
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {filteredPRs.length > 0 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredPRs.length)} of {filteredPRs.length} results
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                >
                  Previous
                </Button>
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      return page === 1 || 
                             page === totalPages || 
                             (page >= currentPage - 1 && page <= currentPage + 1);
                    })
                    .map((page, index, array) => (
                      <div key={page} className="flex items-center">
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <Button
                          onClick={() => setCurrentPage(page)}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          className={currentPage === page ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                        >
                          {page}
                        </Button>
                      </div>
                    ))
                  }
                </div>
                <Button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create PR Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Purchase Request</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePR} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="entity">Entity *</Label>
              <select
                id="entity"
                value={formData.entity_id}
                onChange={(e) => setFormData({ ...formData, entity_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select Entity</option>
                {entities.map(e => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="vendor">Vendor *</Label>
              <select
                id="vendor"
                value={formData.vendor_id}
                onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select Vendor</option>
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.legal_entity_name || v.trade_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Reason *</Label>
              <textarea
                id="reason"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                rows={3}
                placeholder="Enter reason for purchase"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="base_amount">Base Amount (₹) *</Label>
              <Input
                id="base_amount"
                type="number"
                step="0.01"
                value={formData.base_amount}
                onChange={(e) => setFormData({ ...formData, base_amount: e.target.value })}
                placeholder="Enter base amount"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="inclusive_tax">Tax Inclusive? *</Label>
              <select
                id="inclusive_tax"
                value={formData.inclusive_tax}
                onChange={(e) => setFormData({ ...formData, inclusive_tax: e.target.value, tax_amount: e.target.value === 'No' ? '' : formData.tax_amount })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>

            {formData.inclusive_tax === 'Yes' && (
              <div className="space-y-2">
                <Label htmlFor="tax_amount">Tax Amount (₹) *</Label>
                <Input
                  id="tax_amount"
                  type="number"
                  step="0.01"
                  value={formData.tax_amount}
                  onChange={(e) => setFormData({ ...formData, tax_amount: e.target.value })}
                  placeholder="Enter tax amount"
                  required
                />
                {formData.base_amount && formData.tax_amount && (
                  <p className="text-sm text-green-600 font-medium">
                    Total Amount: ₹{((parseFloat(formData.base_amount) || 0) + (parseFloat(formData.tax_amount) || 0)).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="proforma">Proforma Invoice *</Label>
              <Input
                id="proforma"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.zip"
                onChange={(e) => setProformaFile(e.target.files[0])}
                required
              />
              <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, PNG, JPG, ZIP</p>
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
                {loading ? 'Creating...' : 'Create Request'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      {selectedPr && (
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <span>Purchase Request Details</span>
                {getStatusBadge(selectedPr)}
                {selectedPr.hold_status && (
                  <Badge className="bg-red-100 text-red-800 w-auto inline-flex">On Hold</Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* PR ID & PO Number */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">PR ID</p>
                  <p className="text-sm font-medium mt-1">{selectedPr.id}</p>
                </div>
                {selectedPr.po_number && (
                  <div>
                    <p className="text-sm text-gray-600">PO Number</p>
                    <p className="text-sm font-medium mt-1 text-blue-600">{selectedPr.po_number}</p>
                  </div>
                )}
              </div>

              {/* PR Details */}
              {!editMode ? (
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Entity</p>
                    <p className="text-sm font-medium mt-1">{getEntityName(selectedPr.entity_id, selectedPr.entity_name)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Vendor</p>
                    <p className="text-sm font-medium mt-1">{getVendorName(selectedPr.vendor_id)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Cost Centre</p>
                    <p className="text-sm font-medium mt-1">{selectedPr.cost_centre}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Reason</p>
                    <p className="text-sm font-medium mt-1">{selectedPr.reason}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Tax Inclusive</p>
                    <p className="text-sm font-medium mt-1">{selectedPr.inclusive_tax || 'No'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Base Amount</p>
                    <p className="text-sm font-medium mt-1">₹{(selectedPr.base_amount || 0).toLocaleString()}</p>
                  </div>
                  {selectedPr.inclusive_tax === 'Yes' && (
                    <>
                      <div>
                        <p className="text-sm text-gray-600">Tax Amount</p>
                        <p className="text-sm font-medium mt-1">₹{(selectedPr.tax_amount || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total Amount</p>
                        <p className="text-sm font-medium mt-1 text-green-700">₹{(selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}</p>
                      </div>
                    </>
                  )}
                  {/* TDS Related Fields */}
                  {selectedPr.is_tds_applicable === 'Yes' && (
                    <>
                      <div>
                        <p className="text-sm text-gray-600">TDS Applicable</p>
                        <p className="text-sm font-medium mt-1 text-orange-600">Yes</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">TDS Percentage</p>
                        <p className="text-sm font-medium mt-1">{selectedPr.tds_percentage || 0}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Payable Amount</p>
                        <p className="text-sm font-medium mt-1 text-blue-700 font-semibold">
                          ₹{(selectedPr.payable_amount || 0).toLocaleString()}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <form onSubmit={handleEditPR} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Entity *</Label>
                      <select
                        value={editFormData.entity_id}
                        onChange={(e) => setEditFormData({ ...editFormData, entity_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      >
                        <option value="">Select Entity</option>
                        {entities.map(e => (
                          <option key={e.id} value={e.id}>{e.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Vendor</Label>
                      <select
                        value={editFormData.vendor_id}
                        onChange={(e) => setEditFormData({ ...editFormData, vendor_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      >
                        {vendors.map(v => (
                          <option key={v.id} value={v.id}>
                            {v.legal_entity_name || v.trade_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Cost Centre</Label>
                      <select
                        value={editFormData.cost_centre}
                        onChange={(e) => setEditFormData({ ...editFormData, cost_centre: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                        required
                        disabled
                      >
                        {costCentres.map(cc => (
                          <option key={cc} value={cc}>{cc}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Cost Centre cannot be changed after creation</p>
                    </div>
                    <div className="col-span-2">
                      <Label>Reason</Label>
                      <textarea
                        value={editFormData.reason}
                        onChange={(e) => setEditFormData({ ...editFormData, reason: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        rows={3}
                        required
                      />
                    </div>
                    <div>
                      <Label>Base Amount (₹)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editFormData.base_amount}
                        onChange={(e) => setEditFormData({ ...editFormData, base_amount: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Tax Inclusive</Label>
                      <select
                        value={editFormData.inclusive_tax}
                        onChange={(e) => setEditFormData({ ...editFormData, inclusive_tax: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <Label>Replace Proforma Invoice (Optional)</Label>
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.zip"
                        onChange={(e) => setEditProformaFile(e.target.files[0])}
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, PNG, JPG, ZIP</p>
                      {editProformaFile && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ New file selected: {editProformaFile.name}
                        </p>
                      )}
                      {selectedPr.proforma_invoice_url && !editProformaFile && (
                        <p className="text-xs text-gray-500 mt-1">
                          Current file will be replaced if you upload a new one
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setEditMode(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={loading} className="bg-indigo-600">
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              )}

              {/* Advance Payment Details */}
              {selectedPr.is_advance_payment && (
                <div className="bg-amber-50 rounded-lg p-4 space-y-3 border-2 border-amber-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-amber-900">Advance Payment Details</h3>
                    {getAdvanceStatusBadge(selectedPr)}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-amber-700">Amount:</span>
                      <p className="text-sm font-bold text-amber-900">₹{(selectedPr.base_amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-amber-700">Advance Payment:</span>
                      <p className="text-sm font-bold text-green-900 bg-green-50 p-2 rounded">₹{(selectedPr.advance_amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-amber-700">Payment Status:</span>
                      <p className="text-sm font-medium text-amber-900">
                        {selectedPr.is_partial_paid ? 'Advance Paid' : 'Advance Pending'}
                      </p>
                    </div>
                    {selectedPr.advance_approved_by && (
                      <div className="col-span-2 bg-green-50 p-2 rounded">
                        <span className="text-sm font-medium text-green-700">PPA Approved:</span>
                        <p className="text-sm text-green-900">By {selectedPr.advance_approved_by} on {new Date(selectedPr.advance_approved_at).toLocaleString()}</p>
                      </div>
                    )}
                    {selectedPr.advance_rejected_by && (
                      <>
                        <div className="col-span-2 bg-red-50 p-2 rounded">
                          <span className="text-sm font-medium text-red-700">PPA Rejected:</span>
                          <p className="text-sm text-red-900">By {selectedPr.advance_rejected_by} on {new Date(selectedPr.advance_rejected_at).toLocaleString()}</p>
                        </div>
                        <div className="col-span-2 bg-red-100 p-2 rounded">
                          <span className="text-sm font-medium text-red-700">Rejection Reason:</span>
                          <p className="text-sm text-red-900">{selectedPr.advance_rejection_reason}</p>
                        </div>
                      </>
                    )}
                    {selectedPr.advance_utr_number && (
                      <div className="col-span-2 bg-green-50 p-2 rounded">
                        <span className="text-sm font-medium text-green-700">Advance Payment UTR:</span>
                        <p className="text-sm text-green-900">{selectedPr.advance_utr_number}</p>
                        <p className="text-xs text-green-800">Paid on {new Date(selectedPr.advance_paid_at).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>
                  <div className="bg-amber-100 p-3 rounded">
                    <p className="text-xs text-amber-800">
                      <strong>Workflow:</strong> Advance payment will be processed after Finance Head approval. 
                      After delivery confirmation and invoice upload, remaining amount (₹
                      {((selectedPr.base_amount || 0) - (selectedPr.advance_amount || 0)).toLocaleString()}) 
                      will be processed separately.
                    </p>
                  </div>
                </div>
              )}

              {/* Partial Payment Info Section - Show if any partial payments made */}
              {((selectedPr.partial_payments && selectedPr.partial_payments.length > 0) || (selectedPr.total_paid_amount > 0)) && (
                <div className="bg-amber-50 rounded-lg p-4 space-y-3 border-2 border-amber-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-amber-900">Partial Payment Info</h3>
                    <Badge className={selectedPr.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                      {selectedPr.payment_status === 'paid' ? 'Fully Paid' : 'Partly Paid'}
                    </Badge>
                  </div>
                  
                  {/* Balance Amount Summary */}
                  <div className="grid grid-cols-3 gap-4 bg-white p-3 rounded-lg">
                    <div>
                      <p className="text-xs text-gray-600">Payable Amount</p>
                      <p className="text-sm font-bold text-gray-900">
                        ₹{(() => {
                          let totalPayable = selectedPr.payable_amount || selectedPr.total_amount || selectedPr.base_amount || 0;
                          if (selectedPr.is_advance_payment && selectedPr.is_partial_paid) {
                            totalPayable = totalPayable - (selectedPr.advance_amount || 0);
                          }
                          return totalPayable.toLocaleString();
                        })()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Total Paid</p>
                      <p className="text-sm font-bold text-green-700">
                        ₹{(selectedPr.total_paid_amount || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Balance Amount</p>
                      <p className="text-sm font-bold text-amber-700">
                        ₹{(() => {
                          let totalPayable = selectedPr.payable_amount || selectedPr.total_amount || selectedPr.base_amount || 0;
                          if (selectedPr.is_advance_payment && selectedPr.is_partial_paid) {
                            totalPayable = totalPayable - (selectedPr.advance_amount || 0);
                          }
                          return (totalPayable - (selectedPr.total_paid_amount || 0)).toLocaleString();
                        })()}
                      </p>
                    </div>
                  </div>

                  {/* Payment History */}
                  {selectedPr.partial_payments && selectedPr.partial_payments.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-amber-900">Payment History:</p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {selectedPr.partial_payments.map((payment, index) => (
                          <div key={payment.id || index} className="bg-white p-3 rounded-lg border border-amber-200">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-sm font-semibold text-gray-900">
                                  Payment #{index + 1}: ₹{(payment.amount || 0).toLocaleString()}
                                </p>
                                <p className="text-xs text-gray-600">
                                  UTR: {payment.utr_number} | {payment.payment_method}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-600">
                                  {payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : 'N/A'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  by {payment.paid_by}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Documents */}
              <div>
                <h3 className="font-semibold mb-2">Documents</h3>
                <div className="space-y-2">
                  {selectedPr.proforma_invoice_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadFile(
                        selectedPr.proforma_invoice_url, 
                        getFilenameWithExtension(selectedPr.proforma_invoice_url, `Proforma_Invoice_${selectedPr.id}.pdf`)
                      )}
                      className="flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Proforma Invoice
                    </Button>
                  )}
                  {selectedPr.tax_invoice_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadFile(
                        selectedPr.tax_invoice_url, 
                        getFilenameWithExtension(selectedPr.tax_invoice_url, `Tax_Invoice_${selectedPr.id}.pdf`)
                      )}
                      className="flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Tax Invoice
                    </Button>
                  )}
                  {selectedPr.po_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadFile(selectedPr.po_url, `PO_${selectedPr.po_number}.pdf`)}
                      className="flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Purchase Order
                    </Button>
                  )}
                </div>
              </div>

              {/* Tax Invoice Details */}
              {selectedPr.tax_invoice_url && (selectedPr.tax_invoice_date || selectedPr.credit_period || selectedPr.due_date || selectedPr.paid_at || selectedPr.utr_number) && (
                <div className="border-t pt-4">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Tax Invoice Details</h3>
                  <div className="bg-green-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      {selectedPr.tax_invoice_date && (
                        <div>
                          <span className="font-medium text-gray-700">Invoice Date:</span>
                          <p className="text-gray-900 mt-1">
                            {new Date(selectedPr.tax_invoice_date).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {selectedPr.credit_period && (
                        <div>
                          <span className="font-medium text-gray-700">Credit Period:</span>
                          <p className="text-gray-900 mt-1">{selectedPr.credit_period} days</p>
                        </div>
                      )}
                      {selectedPr.due_date && (
                        <div>
                          <span className="font-medium text-gray-700">Due Date:</span>
                          <p className="text-gray-900 mt-1">
                            {new Date(selectedPr.due_date).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {selectedPr.paid_at && (
                        <div>
                          <span className="font-medium text-gray-700">Paid On:</span>
                          <p className="text-gray-900 mt-1">
                            {new Date(selectedPr.paid_at).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {selectedPr.utr_number && (
                        <div>
                          <span className="font-medium text-gray-700">UTR Number:</span>
                          <p className="text-gray-900 mt-1">{selectedPr.utr_number}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                {selectedPr.status === 'additional_info_requested' && (
                  <Button
                    onClick={() => {
                      setReplyDialogOpen(true);
                      setDetailsDialogOpen(false);
                    }}
                    className="bg-indigo-600 flex items-center gap-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Reply
                  </Button>
                )}
              </div>

              {/* Rejected Reason */}
              {selectedPr.status === 'rejected' && selectedPr.rejection_reason && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-900 mb-1">Rejection Reason</p>
                  <p className="text-sm text-red-800">{selectedPr.rejection_reason}</p>
                  {selectedPr.rejected_by && (
                    <p className="text-xs text-red-700 mt-2">Rejected by: {selectedPr.rejected_by}</p>
                  )}
                </div>
              )}

              {/* Hold Reason */}
              {selectedPr.hold_status && selectedPr.hold_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-red-800 mb-2">Hold Reason:</p>
                  {selectedPr.hold_reason_type && (
                    <p className="text-sm font-medium text-red-800 mb-2">Type: {selectedPr.hold_reason_type}</p>
                  )}
                  <p className="text-sm text-red-700">{selectedPr.hold_reason}</p>
                  {selectedPr.held_by && selectedPr.held_at && (
                    <p className="text-xs text-red-600 mt-2">
                      Held by: {selectedPr.held_by} on {new Date(selectedPr.held_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}

              {/* Additional Info Request */}
              {selectedPr.additional_info_request && (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm font-medium text-orange-900 mb-1">Additional Information Requested</p>
                  <p className="text-sm text-orange-800">{selectedPr.additional_info_request}</p>
                </div>
              )}

              {/* Additional Info Conversation */}
              {selectedPr.additional_info_conversation && selectedPr.additional_info_conversation.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-800">Additional Information Conversation</h4>
                  {selectedPr.additional_info_conversation.map((entry, index) => (
                    <div 
                      key={index} 
                      className={`p-3 rounded-lg ${
                        entry.type === 'request' 
                          ? 'bg-orange-50 border border-orange-200' 
                          : 'bg-blue-50 border border-blue-200'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium text-sm text-gray-700">
                          {entry.type === 'request' 
                            ? (entry.role === 'accounts' ? 'Accounts' : 'Finance Head') + ': ' + entry.user
                            : 'You: ' + entry.user
                          }
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(entry.timestamp).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-900">{entry.message}</p>
                      {entry.docs && entry.docs.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <span className="text-xs font-medium text-gray-600">Attachments:</span>
                          {entry.docs.map((doc, docIndex) => (
                            <div key={docIndex}>
                              <Button
                                onClick={() => downloadFile(doc, `Additional_Info_Doc_${docIndex + 1}.pdf`)}
                                variant="link"
                                className="p-0 h-auto text-xs text-blue-600 hover:underline flex items-center gap-1"
                              >
                                <Download className="w-3 h-3" />
                                Download Document {docIndex + 1}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Document Trail */}
              <div className="border-t pt-4">
                <h3 className="text-base font-semibold text-gray-900 mb-3">Document Trail</h3>
                <div className="space-y-2">
                  {/* Purchase Request Created */}
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-gray-900 text-sm">Purchase Request Created</div>
                      <div className="text-xs text-gray-500">
                        by {selectedPr.requested_by} on {new Date(selectedPr.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', month: 'short', day: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Proforma Invoice Uploaded */}
                  {selectedPr.proforma_invoice_url && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Proforma Invoice Uploaded</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.requested_by} on {new Date(selectedPr.created_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Function Approved */}
                  {selectedPr.approved_by && selectedPr.approved_at && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Function Approved</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.approved_by} on {new Date(selectedPr.approved_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* PO Generated */}
                  {selectedPr.po_number && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">PO Generated</div>
                        <div className="text-xs text-gray-500">
                          PO #{selectedPr.po_number}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Delivery Confirmed */}
                  {selectedPr.delivery_confirmed && selectedPr.delivery_confirmed_at && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Delivery Confirmed</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.requested_by} on {new Date(selectedPr.delivery_confirmed_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tax Invoice Uploaded */}
                  {selectedPr.tax_invoice_url && selectedPr.tax_invoice_uploaded_at && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Tax Invoice Uploaded</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.tax_invoice_uploaded_by || selectedPr.requested_by} on {new Date(selectedPr.tax_invoice_uploaded_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Finance Approved */}
                  {selectedPr.finance_approved_by && selectedPr.finance_approved_at && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Finance Approved</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.finance_approved_by} on {new Date(selectedPr.finance_approved_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Payment Completed */}
                  {selectedPr.paid_at && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Payment Completed</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.paid_by} on {new Date(selectedPr.paid_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Additional Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Your Response *</Label>
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md h-32"
                placeholder="Enter your response..."
                required
              />
            </div>
            
            <div>
              <Label>Upload Supporting Documents (Optional)</Label>
              <Input
                type="file"
                multiple
                onChange={(e) => setReplyDocs(Array.from(e.target.files))}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
              {replyDocs.length > 0 && (
                <p className="text-xs text-gray-600 mt-1">
                  {replyDocs.length} file(s) selected
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setReplyDialogOpen(false);
                  setReplyMessage('');
                  setReplyDocs([]);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReplyToInfoRequest}
                disabled={loading}
                className="bg-indigo-600"
              >
                {loading ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Invoice Dialog */}
      <Dialog open={uploadInvoiceDialogOpen} onOpenChange={setUploadInvoiceDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editUploadInvoiceMode ? 'Edit Upload Invoice Request' : 'Upload Invoice Request'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Entity Dropdown */}
            <div>
              <Label>Entity <span className="text-red-500">*</span></Label>
              <select
                value={uploadInvoiceData.entity_id}
                onChange={(e) => setUploadInvoiceData({...uploadInvoiceData, entity_id: e.target.value})}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              >
                <option value="">Select Entity</option>
                {entities.map(e => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>

            {/* Vendor Dropdown */}
            <div>
              <Label>Vendor <span className="text-red-500">*</span></Label>
              <select
                value={uploadInvoiceData.vendor_id}
                onChange={(e) => setUploadInvoiceData({...uploadInvoiceData, vendor_id: e.target.value})}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select Vendor</option>
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>{v.legal_entity_name}</option>
                ))}
              </select>
            </div>
            
            {/* Reason */}
            <div>
              <Label>Reason <span className="text-red-500">*</span></Label>
              <textarea
                value={uploadInvoiceData.reason}
                onChange={(e) => setUploadInvoiceData({...uploadInvoiceData, reason: e.target.value})}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                rows={3}
                placeholder="Enter reason for this invoice..."
              />
            </div>
            
            {/* Amount */}
            <div>
              <Label>
                {uploadInvoiceData.inclusive_tax === 'Yes' ? 'Base Amount' : 'Amount'} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={uploadInvoiceData.base_amount}
                onChange={(e) => {
                  const baseAmt = parseFloat(e.target.value) || 0;
                  const taxAmt = parseFloat(uploadInvoiceData.tax_amount) || 0;
                  setUploadInvoiceData({
                    ...uploadInvoiceData, 
                    base_amount: e.target.value,
                    total_amount: (baseAmt + taxAmt).toFixed(2)
                  });
                }}
                placeholder={uploadInvoiceData.inclusive_tax === 'Yes' ? 'Enter base amount' : 'Enter amount'}
                className="mt-1"
              />
            </div>
            
            {/* Tax Inclusive */}
            <div>
              <Label>Tax Inclusive? <span className="text-red-500">*</span></Label>
              <select
                value={uploadInvoiceData.inclusive_tax}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setUploadInvoiceData({
                    ...uploadInvoiceData, 
                    inclusive_tax: newValue,
                    tax_amount: newValue === 'Yes' ? '0' : '',
                    total_amount: newValue === 'Yes' ? uploadInvoiceData.base_amount : ''
                  });
                }}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>

            {/* Show Tax Fields if Tax Inclusive is Yes */}
            {uploadInvoiceData.inclusive_tax === 'Yes' && (
              <>
                {/* Tax Amount */}
                <div>
                  <Label>Tax Amount (₹) <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={uploadInvoiceData.tax_amount}
                    onChange={(e) => {
                      const taxAmt = parseFloat(e.target.value) || 0;
                      const baseAmt = parseFloat(uploadInvoiceData.base_amount) || 0;
                      setUploadInvoiceData({
                        ...uploadInvoiceData, 
                        tax_amount: e.target.value,
                        total_amount: (baseAmt + taxAmt).toFixed(2)
                      });
                    }}
                    placeholder="Enter tax amount (manual entry)"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Manually enter the tax amount from invoice</p>
                </div>

                {/* Total Amount (Read-only) */}
                <div>
                  <Label>Total Amount (₹)</Label>
                  <Input
                    type="text"
                    value={uploadInvoiceData.total_amount}
                    readOnly
                    className="mt-1 bg-gray-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-calculated: Base Amount + Tax Amount</p>
                </div>
              </>
            )}
            
            {/* Invoice Date */}
            <div>
              <Label>Invoice Date <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                max={new Date().toISOString().split('T')[0]}
                value={uploadInvoiceData.invoice_date}
                onChange={(e) => setUploadInvoiceData({...uploadInvoiceData, invoice_date: e.target.value})}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Future dates are not allowed</p>
            </div>
            
            {/* Invoice File */}
            <div>
              <Label>Invoice File {!editUploadInvoiceMode && <span className="text-red-500">*</span>}</Label>
              <Input
                type="file"
                onChange={(e) => setUploadInvoiceFile(e.target.files[0])}
                accept=".pdf,.png,.jpg,.jpeg,.zip"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                {editUploadInvoiceMode ? 'Upload new file to replace existing invoice' : 'Accepted formats: PDF, PNG, JPG, ZIP'}
              </p>
              {uploadInvoiceFile && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ {uploadInvoiceFile.name}
                </p>
              )}
            </div>
            
            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This request will be sent to your function approver for review. 
                Upon approval, it will be forwarded to Finance for payment processing.
              </p>
            </div>
            
            {/* Submit Button */}
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setUploadInvoiceDialogOpen(false);
                  setUploadInvoiceData({
                    entity_id: '',
                    vendor_id: '',
                    reason: '',
                    base_amount: '',
                    inclusive_tax: 'No',
                    invoice_date: '',
                    tax_amount: '',
                    total_amount: ''
                  });
                  setUploadInvoiceFile(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUploadInvoiceSubmit}
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {loading ? (editUploadInvoiceMode ? 'Updating...' : 'Submitting...') : (editUploadInvoiceMode ? 'Update Request' : 'Submit Request')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Delivery & Upload Tax Invoice Dialog */}
      <Dialog open={confirmDeliveryDialogOpen} onOpenChange={setConfirmDeliveryDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Confirm Delivery & Upload Tax Invoice</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                <strong>Step 1:</strong> Confirming that you have received the goods/services
              </p>
              <p className="text-sm text-green-800 mt-1">
                <strong>Step 2:</strong> Upload the tax invoice from the vendor
              </p>
            </div>

            {/* Two Column Layout for Tax Fields */}
            {selectedPrForDelivery?.inclusive_tax === 'Yes' && (
              <div className="grid grid-cols-2 gap-4">
                {/* Left Column */}
                <div className="space-y-4">
                  {/* Base Amount */}
                  <div>
                    <Label>Base Amount (₹) <span className="text-red-500">*</span></Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={taxInvoiceData.base_amount}
                      onChange={(e) => {
                        const baseAmt = parseFloat(e.target.value) || 0;
                        const taxAmt = parseFloat(taxInvoiceData.tax_amount) || 0;
                        setTaxInvoiceData({ 
                          ...taxInvoiceData, 
                          base_amount: e.target.value,
                          total_amount: (baseAmt + taxAmt).toFixed(2)
                        });
                      }}
                      placeholder="Enter base amount"
                      className="mt-1"
                    />
                  </div>

                  {/* Tax Amount */}
                  <div>
                    <Label>Tax Amount (₹) <span className="text-red-500">*</span></Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={taxInvoiceData.tax_amount}
                      onChange={(e) => {
                        const taxAmt = parseFloat(e.target.value) || 0;
                        const baseAmt = parseFloat(taxInvoiceData.base_amount) || 0;
                        setTaxInvoiceData({ 
                          ...taxInvoiceData, 
                          tax_amount: e.target.value,
                          total_amount: (baseAmt + taxAmt).toFixed(2)
                        });
                      }}
                      placeholder="Enter tax amount (manual entry)"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Manually enter the tax amount from invoice</p>
                  </div>

                  {/* Total Amount (Read-only) */}
                  <div>
                    <Label>Total Amount (₹)</Label>
                    <Input
                      type="text"
                      value={taxInvoiceData.total_amount}
                      readOnly
                      className="mt-1 bg-gray-50"
                    />
                    <p className="text-xs text-gray-500 mt-1">Auto-calculated: Base Amount + Tax Amount</p>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  {/* Invoice Date */}
                  <div>
                    <Label>Invoice Date <span className="text-red-500">*</span></Label>
                    <Input
                      type="date"
                      max={new Date().toISOString().split('T')[0]}
                      value={taxInvoiceData.invoice_date}
                      onChange={(e) => setTaxInvoiceData({ ...taxInvoiceData, invoice_date: e.target.value })}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Future dates are not allowed</p>
                  </div>
                  
                  {/* Tax Invoice File */}
                  <div>
                    <Label>Tax Invoice File <span className="text-red-500">*</span></Label>
                    <Input
                      type="file"
                      onChange={(e) => setTaxInvoiceFile(e.target.files[0])}
                      accept=".pdf,.png,.jpg,.jpeg,.zip"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, PNG, JPG, ZIP</p>
                    {taxInvoiceFile && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ {taxInvoiceFile.name}
                      </p>
                    )}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-800">
                      <strong>Note:</strong> Credit period will be automatically applied from vendor details. 
                      Due date will be calculated based on invoice date and credit period.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Single Column Layout when Tax Inclusive is No */}
            {selectedPrForDelivery?.inclusive_tax !== 'Yes' && (
              <>
                {/* Invoice Date */}
                <div>
                  <Label>Invoice Date <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    max={new Date().toISOString().split('T')[0]}
                    value={taxInvoiceData.invoice_date}
                    onChange={(e) => setTaxInvoiceData({ ...taxInvoiceData, invoice_date: e.target.value })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Future dates are not allowed</p>
                </div>
                
                {/* Tax Invoice File */}
                <div>
                  <Label>Tax Invoice File <span className="text-red-500">*</span></Label>
                  <Input
                    type="file"
                    onChange={(e) => setTaxInvoiceFile(e.target.files[0])}
                    accept=".pdf,.png,.jpg,.jpeg,.zip"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, PNG, JPG, ZIP</p>
                  {taxInvoiceFile && (
                    <p className="text-xs text-green-600 mt-1">
                      ✓ {taxInvoiceFile.name}
                    </p>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Credit period will be automatically applied from vendor details. 
                    Due date will be calculated based on invoice date and credit period.
                  </p>
                </div>
              </>
            )}
            
            {/* Submit Buttons */}
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setConfirmDeliveryDialogOpen(false);
                  setTaxInvoiceData({ 
                    invoice_date: '',
                    base_amount: '',
                    tax_amount: '',
                    total_amount: ''
                  });
                  setTaxInvoiceFile(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDeliveryAndUploadInvoice}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? 'Processing...' : 'Confirm & Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Advance Request Dialog */}
      <Dialog open={advanceRequestDialogOpen} onOpenChange={setAdvanceRequestDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedPr && selectedPr.is_advance_payment && selectedPr.status === 'rejected' 
                ? 'Edit Advance Payment Request' 
                : 'Raise Advance Payment Request'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Info Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Advance Payment:</strong> Request partial payment before delivery. 
                Upon approval, PO will be issued and advance payment will require Finance Head approval.
              </p>
            </div>

            {/* Entity Dropdown */}
            <div>
              <Label>Entity <span className="text-red-500">*</span></Label>
              <select
                value={advanceRequestData.entity_id}
                onChange={(e) => setAdvanceRequestData({...advanceRequestData, entity_id: e.target.value})}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                required
              >
                <option value="">Select Entity</option>
                {entities.map(e => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>

            {/* Vendor Dropdown */}
            <div>
              <Label>Vendor <span className="text-red-500">*</span></Label>
              <select
                value={advanceRequestData.vendor_id}
                onChange={(e) => setAdvanceRequestData({...advanceRequestData, vendor_id: e.target.value})}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="">Select Vendor</option>
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>{v.legal_entity_name}</option>
                ))}
              </select>
            </div>
            
            {/* Reason */}
            <div>
              <Label>Reason <span className="text-red-500">*</span></Label>
              <textarea
                value={advanceRequestData.reason}
                onChange={(e) => setAdvanceRequestData({...advanceRequestData, reason: e.target.value})}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                rows={3}
                placeholder="Enter reason for this purchase..."
              />
            </div>
            
            {/* Base Amount */}
            <div>
              <Label>
                {advanceRequestData.inclusive_tax === 'Yes' ? 'Base Amount' : 'Total Amount'} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={advanceRequestData.base_amount}
                onChange={(e) => {
                  const baseAmt = parseFloat(e.target.value) || 0;
                  const taxAmt = parseFloat(advanceRequestData.tax_amount) || 0;
                  setAdvanceRequestData({
                    ...advanceRequestData, 
                    base_amount: e.target.value,
                    total_amount: (baseAmt + taxAmt).toFixed(2)
                  });
                }}
                placeholder={advanceRequestData.inclusive_tax === 'Yes' ? 'Enter base amount' : 'Enter total amount'}
                className="mt-1"
              />
            </div>
            
            {/* Tax Inclusive */}
            <div>
              <Label>Tax Inclusive? <span className="text-red-500">*</span></Label>
              <select
                value={advanceRequestData.inclusive_tax}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setAdvanceRequestData({
                    ...advanceRequestData, 
                    inclusive_tax: newValue,
                    tax_amount: newValue === 'Yes' ? '0' : '',
                    total_amount: newValue === 'Yes' ? advanceRequestData.base_amount : advanceRequestData.base_amount
                  });
                }}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>

            {/* Show Tax Fields if Tax Inclusive is Yes */}
            {advanceRequestData.inclusive_tax === 'Yes' && (
              <>
                {/* Tax Amount */}
                <div>
                  <Label>Tax Amount (₹) <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={advanceRequestData.tax_amount}
                    onChange={(e) => {
                      const taxAmt = parseFloat(e.target.value) || 0;
                      const baseAmt = parseFloat(advanceRequestData.base_amount) || 0;
                      setAdvanceRequestData({
                        ...advanceRequestData, 
                        tax_amount: e.target.value,
                        total_amount: (baseAmt + taxAmt).toFixed(2)
                      });
                    }}
                    placeholder="Enter tax amount (manual entry)"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Manually enter the tax amount from invoice</p>
                </div>

                {/* Total Amount (Read-only) */}
                <div>
                  <Label>Total Amount (₹)</Label>
                  <Input
                    type="text"
                    value={advanceRequestData.total_amount}
                    readOnly
                    className="mt-1 bg-gray-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-calculated: Base Amount + Tax Amount</p>
                </div>
              </>
            )}
            
            {/* Advance Amount */}
            <div>
              <Label>Advance Payment Amount <span className="text-red-500">*</span></Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={advanceRequestData.total_amount || advanceRequestData.base_amount}
                value={advanceRequestData.advance_amount}
                onChange={(e) => setAdvanceRequestData({...advanceRequestData, advance_amount: e.target.value})}
                placeholder="Enter advance amount"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Amount to be paid in advance (before delivery)
              </p>
              {advanceRequestData.total_amount && advanceRequestData.advance_amount && (
                <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Pending Amount (after delivery):</strong> ₹
                    {(parseFloat(advanceRequestData.total_amount) - parseFloat(advanceRequestData.advance_amount)).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
            
            {/* Proforma Invoice File */}
            <div>
              <Label>Proforma Invoice <span className="text-red-500">*</span></Label>
              <Input
                type="file"
                onChange={(e) => setAdvanceProformaFile(e.target.files[0])}
                accept=".pdf,.png,.jpg,.jpeg,.zip"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, PNG, JPG, ZIP</p>
              {advanceProformaFile && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ {advanceProformaFile.name}
                </p>
              )}
            </div>
            
            {/* Workflow Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-medium text-blue-900 mb-2">Workflow:</p>
              <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
                <li>Sent to Function Approver for approval</li>
                <li>Upon approval, PO will be generated</li>
                <li>Finance Head approval required for advance payment (PPA)</li>
                <li>Finance Team will process the advance payment</li>
                <li>After delivery, remaining amount will be processed</li>
              </ol>
            </div>
            
            {/* Submit Buttons */}
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setAdvanceRequestDialogOpen(false);
                  setAdvanceRequestData({
                    entity_id: '',
                    vendor_id: '',
                    reason: '',
                    base_amount: '',
                    inclusive_tax: 'No',
                    advance_amount: ''
                  });
                  setAdvanceProformaFile(null);
                  setSelectedPr(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAdvanceRequestSubmit}
                disabled={loading}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {loading ? 'Processing...' : 
                 (selectedPr && selectedPr.is_advance_payment && selectedPr.status === 'rejected' 
                   ? 'Update Advance Request' 
                   : 'Submit Advance Request')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseRequest;
